import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { ISubscriptionItem, IUserAccessPermissions } from './models';
import useFetchUserAccessPermissions from './hooks/useFetchUserAccesspermissions';
import { UsersApi } from '../../api/usersApi';
import { PopupOrPanel } from '../../components/popupsAndPanels/PopupAndPanels';
import { FormBooleanField } from 'components/formComponents/FormBooleanField';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import ErrorMessage from 'components/errorMessage/ErrorMessage';
import { ERROR_MESSAGE } from 'appConstants';
import { useGetDashboardList } from 'pages/dashboard/useGetDashboardList';
import { Spinner } from 'components/spinner/Spinner';

type Props = {
  panelOpen: boolean;
  togglePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  id: number;
};

export const AccessPermission: React.FC<Props> = ({
  panelOpen,
  togglePanelOpen,
  id,
}) => {
  const [initialValues, setInitialValues] =
    useState<IUserAccessPermissions | null>(null);
  const [error, setError] = useState<string>('');
  const [detailsError, setDetailsError] = useState<string>('');

  const validationSchemaAddUser = Yup.object({
    bGetSet4PE: Yup.boolean().required('Required field'),
    bGetSet4Music: Yup.boolean().required('Required field'),
  });

  useFetchUserAccessPermissions(id, setInitialValues, setDetailsError);

  const { dashboardListInfo, error: subscriptionsListError } =
    useGetDashboardList();

  const [subscriptionsList, setSubscriptionList] = useState<
    ISubscriptionItem[] | null
  >(null);

  useEffect(() => {
    if (dashboardListInfo) {
      const subjectsList = dashboardListInfo.map((item) => ({
        label: item.subject,
        fieldName: `b${item.subject.split(' ').join('')}`,
      }));
      setSubscriptionList(subjectsList);
    }
  }, [dashboardListInfo]);

  const submitAccessPermissions = async (
    values: IUserAccessPermissions,
    { setSubmitting }: FormikHelpers<IUserAccessPermissions>
  ) => {
    setError('');
    setSubmitting(true);
    const result = await UsersApi.submitAccessPermissions(id, values);
    if (result.data) {
      setSubmitting(false);
      togglePanelOpen(false);
    }

    if (result.error) {
      const error = result.error.message;
      setError(error);
      setSubmitting(false);
    }
  };

  if (detailsError)
    return (
      <PopupOrPanel
        isPopup={false}
        isOpen={panelOpen}
        hdrTitle={'Access permission'}
        position="right"
        primaryBtnTxt="Close"
        secondaryBtnTxt="Cancel"
        givenClass="AccessPermissionManagedUser"
        handleClose={() => togglePanelOpen(false)}
        handlePrimaryBtn={() => togglePanelOpen(false)}
        handleSecondaryBtn={() => togglePanelOpen(false)}
        width={560}
      >
        <ErrorMessage message={ERROR_MESSAGE} />
      </PopupOrPanel>
    );

  if (initialValues)
    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchemaAddUser}
        onSubmit={(values, formikHelpers) =>
          submitAccessPermissions(values, formikHelpers)
        }
      >
        {({ values, submitForm, setFieldValue, isSubmitting }) => (
          <PopupOrPanel
            isPopup={false}
            isOpen={panelOpen}
            hdrTitle={'Access permission'}
            position="right"
            primaryBtnTxt="Apply"
            secondaryBtnTxt="Cancel"
            givenClass="AccessPermissionManagedUser"
            handleClose={() => togglePanelOpen(false)}
            handlePrimaryBtn={submitForm}
            handleSecondaryBtn={() => togglePanelOpen(false)}
            width={560}
            isSubmitting={isSubmitting}
          >
            {subscriptionsList ? (
              <Form>
                {error && <FormGeneralError message={error} />}
                {subscriptionsList.map((item: any, index: number) => (
                  <FormBooleanField
                    key={index}
                    fieldName={item.fieldName}
                    label={item.label}
                    labelPosition="left"
                  />
                ))}
              </Form>
            ) : (
              <Spinner positionAbsolute />
            )}
          </PopupOrPanel>
        )}
      </Formik>
    );
  return null;
};
